import { ReactElement } from "react";
import { Control, ControllerProps, FieldPath } from "react-hook-form";

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/_atoms/form";
import { NumberInput } from "@/components/_atoms/number-input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/_atoms/select";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { MunicipalPropertyCreateFormValues } from "@/domain/municipal-property/models/municipal-property-create-form-values";
import {
  MUNICIPAL_PROPERTY_MASTER_DATA_BOUNDS,
  MUNICIPAL_PROPERTY_TYPE_LIST,
  MunicipalPropertyType,
} from "@ark/shared";

import { MunicipalPropertyTextField } from "./municipal-property-text-field";

interface AddressField {
  name: "street" | "houseNumber" | "postalCode" | "city";
  maxLength: number;
  className?: string;
}

type MasterDataFieldRenderProps<Name extends FieldPath<MunicipalPropertyCreateFormValues>> = Parameters<
  ControllerProps<MunicipalPropertyCreateFormValues, Name>["render"]
>[0];

const ADDRESS_FIELD_LIST: readonly AddressField[] = [
  { name: "street", maxLength: MUNICIPAL_PROPERTY_MASTER_DATA_BOUNDS.streetMaxLength, className: "sm:col-span-2" },
  { name: "houseNumber", maxLength: MUNICIPAL_PROPERTY_MASTER_DATA_BOUNDS.houseNumberMaxLength },
  { name: "postalCode", maxLength: MUNICIPAL_PROPERTY_MASTER_DATA_BOUNDS.postalCodeMaxLength },
  { name: "city", maxLength: MUNICIPAL_PROPERTY_MASTER_DATA_BOUNDS.cityMaxLength, className: "sm:col-span-2" },
];

interface MunicipalPropertyMasterDataFieldsProps {
  control: Control<MunicipalPropertyCreateFormValues>;
  shouldFocusName?: boolean;
}

export function MunicipalPropertyMasterDataFields({
  control,
  shouldFocusName = false,
}: MunicipalPropertyMasterDataFieldsProps): ReactElement {
  const { t }: ReturnType<typeof useDomainTranslation> = useDomainTranslation("municipal-property");

  return (
    <>
      <fieldset className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
        <legend className="text-sm font-medium">{t("create.section.masterData")}</legend>
        <MunicipalPropertyTextField
          control={control}
          name="name"
          maxLength={MUNICIPAL_PROPERTY_MASTER_DATA_BOUNDS.nameMaxLength}
          autoFocus={shouldFocusName}
        />
        <FormField
          control={control}
          name="type"
          render={({ field }: MasterDataFieldRenderProps<"type">): ReactElement => (
            <FormItem>
              <FormLabel>{t("create.field.type")}</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t("create.field.typePlaceholder")} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectGroup>
                    {MUNICIPAL_PROPERTY_TYPE_LIST.map(
                      (propertyType: MunicipalPropertyType): ReactElement => (
                        <SelectItem key={propertyType} value={propertyType}>
                          {t(`type.${propertyType}`)}
                        </SelectItem>
                      ),
                    )}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <MunicipalPropertyTextField
          control={control}
          name="description"
          maxLength={MUNICIPAL_PROPERTY_MASTER_DATA_BOUNDS.descriptionMaxLength}
          isMultiline
          className="sm:col-span-2"
        />
        <div className="grid grid-cols-1 gap-4 sm:col-span-2 sm:grid-cols-2">
          <FormField
            control={control}
            name="usableFloorAreaM2"
            render={({ field }: MasterDataFieldRenderProps<"usableFloorAreaM2">): ReactElement => (
              <FormItem>
                <FormLabel>{t("create.field.usableFloorAreaM2")}</FormLabel>
                <FormControl>
                  <NumberInput value={field.value} onValueChange={field.onChange} allowDecimals inputMode="decimal" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="commissioningYear"
            render={({ field }: MasterDataFieldRenderProps<"commissioningYear">): ReactElement => (
              <FormItem>
                <FormLabel>{t("create.field.commissioningYear")}</FormLabel>
                <FormControl>
                  <NumberInput value={field.value} onValueChange={field.onChange} inputMode="numeric" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-3">
        <legend className="text-sm font-medium">{t("create.section.address")}</legend>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {ADDRESS_FIELD_LIST.map(
            (addressField: AddressField): ReactElement => (
              <MunicipalPropertyTextField key={addressField.name} control={control} {...addressField} />
            ),
          )}
        </div>
      </fieldset>
    </>
  );
}
